import * as React from 'react'
import { Link } from 'react-router-dom'

import {
  AP_SLUG,
  BUILDING_AI_KNOWLEDGE_SLUG,
  COLLEGE_BOARD_SLUG,
  INTERDISCIPLINARY_SCIENCE,
  PRE_AP_SLUG,
  SOCIAL_STUDIES_SLUG,
  SPRING_BOARD_SLUG,
  WORLD_HISTORY_1200_TO_PRESENT_SLUG,
} from './assignmentFlowConstants'
import LeavingModal from './leaving_modal'
import parsedQueryParams from './parsedQueryParams'

const quillLogoGreenSrc = `${window.postbuildEnv.CDN_URL}/images/logos/quill-logo-green.svg`

interface AssignmentFlowNavigationProps {
  button?: JSX.Element
  courseSlug?: string
  isFromDiagnosticPath?: boolean
  unitTemplateId?: string
  unitTemplateName?: string
}

const learningProcessSlug = 'learning-process'
const diagnosticSlug = 'diagnostic'
const activityTypeSlug = 'activity-type'
const collegeBoardSlug = COLLEGE_BOARD_SLUG
const activityLibrarySlug = 'activity-library'
const selectClassesSlug = 'select-classes'
const featuredActivityPacksSlug = 'featured-activity-packs'
const preApSlug = PRE_AP_SLUG
const apSlug = AP_SLUG
const springBoardSlug = SPRING_BOARD_SLUG
const socialStudiesSlug = SOCIAL_STUDIES_SLUG
const worldHistory1200ToPresentSlug = WORLD_HISTORY_1200_TO_PRESENT_SLUG
const interdisciplinaryScienceSlug = INTERDISCIPLINARY_SCIENCE
const buildingAIKnowledgeSlug = BUILDING_AI_KNOWLEDGE_SLUG

const slash = (index: number) => (
  <span
    aria-hidden="true"
    className="slash"
    key={index}
  >
    /
  </span>
)

const learningProcess = () => (
  <a
    className="focus-on-light"
    href="/assign"
    key="learning-process"
  >
    Learning process
  </a>
)

const diagnostic = () => (
  <Link
    className="focus-on-light"
    key="diagnostic"
    to={`/assign/${diagnosticSlug}`}
  >
    Diagnostic
  </Link>
)

const activityType = () => (
  <Link
    className="focus-on-light"
    key="activity-type"
    to={`/assign/${activityTypeSlug}`}
  >
    Activity type
  </Link>
)

const collegeBoard = () => (
  <Link
    className="focus-on-light"
    key="college-board"
    to={`/assign/${collegeBoardSlug}`}
  >
    College Board
  </Link>
)

const createActivityPack = () => (
  <Link
    className="focus-on-light"
    key="custom-activity-pack"
    to={`/assign/${activityLibrarySlug}`}
  >
    Activity library
  </Link>
)

const selectClasses = () => (
  <Link
    className="focus-on-light"
    key="assign-"
    to={`/assign/${selectClassesSlug}`}
  >
    Assign
  </Link>
)

const activityPack = () => (
  <Link
    className="focus-on-light"
    key="activity-pack"
    to={`/assign/${featuredActivityPacksSlug}`}
  >
    Activity pack
  </Link>
)

const preAp = () => (
  <Link
    className="focus-on-light"
    key="activity-pack"
    to={`/assign/${preApSlug}`}
  >
    Pre-AP
  </Link>
)

const ap = () => (
  <Link
    className="focus-on-light"
    key="activity-pack"
    to={`/assign/${apSlug}`}
  >
    AP
  </Link>
)

const springBoard = () => (
  <Link
    className="focus-on-light"
    key="activity-pack"
    to={`/assign/${springBoardSlug}`}
  >
    SpringBoard
  </Link>
)

const socialStudies = () => (
  <Link
    className="focus-on-light"
    key="social-studies"
    to={`/assign/${socialStudiesSlug}`}
  >
    Social Studies
  </Link>
)

const worldHistory1200ToPresent = () => (
  <Link
    className="focus-on-light"
    key="world-history-1200-to-present"
    to={`/assign/${socialStudiesSlug}/${worldHistory1200ToPresentSlug}`}
  >
    World History: 1200 CE - Present
  </Link>
)

const science = () => (
  <Link
    className="focus-on-light"
    key="science"
    to={`/assign/${interdisciplinaryScienceSlug}`}
  >
    Science
  </Link>
)

const buildingAIKnowledge = () => (
  <Link
    className="focus-on-light"
    key="building-ai-knowledge"
    to={`/assign/${interdisciplinaryScienceSlug}/${buildingAIKnowledgeSlug}`}
  >
    Building AI Knowledge
  </Link>
)

const STEP_ONE = 'step-one'
const STEP_TWO = 'step-two'
const STEP_THREE = 'step-three'
const STEP_FOUR = 'step-four'
const STEP_FIVE = 'step-five'

const individualFeaturedActivityPack = (unitTemplateId: string, unitTemplateName: string) => {
  let link = `/assign/${featuredActivityPacksSlug}/${unitTemplateId}`
  const collegeBoardActivityTypeSlug = parsedQueryParams()[collegeBoardSlug]
  if (collegeBoardActivityTypeSlug) {
    link += `?${collegeBoardSlug}=${collegeBoardActivityTypeSlug}`
  }
  return (
    <Link
      className="focus-on-light"
      key="featured-activity-pack"
      to={link}
    >
      {unitTemplateName}
    </Link>
  )
}

const collegeBoardSlugsToLinks = {
  [preApSlug]: preAp(),
  [apSlug]: ap(),
  [springBoardSlug]: springBoard(),
}

const contentHubSlugsToLinks = {
  [WORLD_HISTORY_1200_TO_PRESENT_SLUG]: worldHistory1200ToPresent(),
  [BUILDING_AI_KNOWLEDGE_SLUG]: buildingAIKnowledge(),
}

const routeLinks = {
  [learningProcessSlug]: () => [slash(1), learningProcess()],
  [diagnosticSlug]: () => [slash(1), learningProcess(), slash(2), diagnostic()],
  [activityTypeSlug]: () => [slash(1), learningProcess(), slash(2), activityType()],
  [preApSlug]: () => [slash(1), learningProcess(), slash(2), collegeBoard(), slash(3), preAp()],
  [apSlug]: () => [slash(1), learningProcess(), slash(2), collegeBoard(), slash(3), ap()],
  [springBoardSlug]: () => [slash(1), learningProcess(), slash(2), collegeBoard(), slash(3), springBoard()],
  [collegeBoardSlug]: () => [slash(1), learningProcess(), slash(2), collegeBoard()],
  [activityLibrarySlug]: () => [slash(1), learningProcess(), slash(2), createActivityPack()],
  [socialStudiesSlug]: ({ courseSlug }: { courseSlug?: string }) => {
    if (courseSlug) {
      return [slash(1), learningProcess(), slash(2), socialStudies(), slash(3), contentHubSlugsToLinks[courseSlug]]
    } else {
      return [slash(1), learningProcess(), slash(2), socialStudies()]
    }
  },
  [interdisciplinaryScienceSlug]: ({ courseSlug }: { courseSlug?: string }) => {
    if (courseSlug) {
      return [slash(1), learningProcess(), slash(2), science(), slash(3), contentHubSlugsToLinks[courseSlug]]
    } else {
      return [slash(1), learningProcess(), slash(2), science()]
    }
  },
  [selectClassesSlug]: ({
    unitTemplateId,
    unitTemplateName,
    isFromDiagnosticPath,
  }: {
    unitTemplateId?: string
    unitTemplateName?: string
    isFromDiagnosticPath?: boolean
  }) => {
    if (isFromDiagnosticPath) {
      return [
        slash(1),
        learningProcess(),
        slash(2),
        diagnostic(),
        slash(3),
        selectClasses(),
      ]
    }

    const collegeBoardActivityTypeSlug = parsedQueryParams()[collegeBoardSlug]
    if (collegeBoardActivityTypeSlug) {
      return [
        slash(1),
        learningProcess(),
        slash(2),
        collegeBoard(),
        slash(3),
        collegeBoardSlugsToLinks[collegeBoardActivityTypeSlug],
        slash(4),
        individualFeaturedActivityPack(unitTemplateId!, unitTemplateName!),
        slash(5),
        selectClasses(),
      ]
    }

    const base = [slash(1), learningProcess(), slash(2), activityType(), slash(3)]
    if (unitTemplateId && unitTemplateName) {
      return base.concat([
        activityPack(),
        slash(4),
        individualFeaturedActivityPack(unitTemplateId, unitTemplateName),
        slash(5),
        selectClasses(),
      ])
    }
    return base.concat([createActivityPack(), slash(4), selectClasses()])
  },
  [featuredActivityPacksSlug]: ({
    unitTemplateId,
    unitTemplateName,
  }: {
    unitTemplateId?: string
    unitTemplateName?: string
  }) => {
    const collegeBoardActivityTypeSlug = parsedQueryParams()[collegeBoardSlug]
    if (collegeBoardActivityTypeSlug) {
      return [
        slash(1),
        learningProcess(),
        slash(2),
        collegeBoard(),
        slash(3),
        collegeBoardSlugsToLinks[collegeBoardActivityTypeSlug],
        slash(4),
        individualFeaturedActivityPack(unitTemplateId!, unitTemplateName!),
      ]
    }

    const base = [slash(1), learningProcess(), slash(2), activityType(), slash(3), activityPack()]
    if (unitTemplateId && unitTemplateName) {
      return base.concat([slash(4), individualFeaturedActivityPack(unitTemplateId, unitTemplateName)])
    }
    return base
  },
}

const routeProgress = {
  [learningProcessSlug]: () => STEP_ONE,
  [diagnosticSlug]: () => STEP_TWO,
  [activityTypeSlug]: () => STEP_TWO,
  [collegeBoardSlug]: () => STEP_TWO,
  [socialStudiesSlug]: ({ courseSlug }: { courseSlug?: string }) => (courseSlug ? STEP_THREE : STEP_TWO),
  [interdisciplinaryScienceSlug]: ({ courseSlug }: { courseSlug?: string }) =>
    courseSlug ? STEP_THREE : STEP_TWO,
  [worldHistory1200ToPresentSlug]: () => STEP_THREE,
  [buildingAIKnowledgeSlug]: () => STEP_THREE,
  [preApSlug]: () => STEP_THREE,
  [apSlug]: () => STEP_THREE,
  [springBoardSlug]: () => STEP_THREE,
  [activityLibrarySlug]: () => STEP_THREE,
  [selectClassesSlug]: () => STEP_FIVE,
  [featuredActivityPacksSlug]: ({
    unitTemplateId,
    unitTemplateName,
  }: {
    unitTemplateId?: string
    unitTemplateName?: string
  }) => {
    if (unitTemplateId && unitTemplateName) {
      return STEP_FOUR
    }
    return STEP_THREE
  },
}

export default class AssignmentFlowNavigation extends React.Component<
  AssignmentFlowNavigationProps,
  any
> {
  constructor(props: AssignmentFlowNavigationProps) {
    super(props)

    this.state = {
      showLeavingModal: false,
    }
  }

  toggleLeavingModal = () => {
    this.setState({ showLeavingModal: !this.state.showLeavingModal })
  }

  getSlug() {
    const path = location.pathname
    // will grab whatever comes after '/assign/' and before another slash
    return path.split('/')[2]
  }

  renderButton() {
    const { button } = this.props
    return button ? button : null
  }

  renderLinks() {
    const { courseSlug, isFromDiagnosticPath, unitTemplateId, unitTemplateName } = this.props
    let elements
    try {
      elements = routeLinks[this.getSlug()]({
        courseSlug,
        isFromDiagnosticPath,
        unitTemplateId,
        unitTemplateName,
      })
    } catch {
      elements = []
    }

    return (
      <ol className="links">
        {elements.map((element, index) => {
          const isSlash =
            element.props &&
            element.props.className &&
            element.props.className.includes('slash')
          return (
            <li
              key={index}
              {...(isSlash ? { 'aria-hidden': 'true', role: 'presentation' } : {})}
            >
              {element}
            </li>
          )
        })}
      </ol>
    )
  }

  renderProgressBar() {
    const { courseSlug, unitTemplateId, unitTemplateName } = this.props
    let className
    try {
      className = routeProgress[this.getSlug()]({
        courseSlug,
        unitTemplateId,
        unitTemplateName,
      })
    } catch {
      className = null
    }
    return (
      <div
        aria-hidden="true"
        className="progress-bar"
      >
        <div className={className} />
      </div>
    )
  }

  renderLeavingModal() {
    if (this.state.showLeavingModal) {
      return <LeavingModal cancel={this.toggleLeavingModal} />
    }
  }

  render() {
    return (
      <nav
        aria-label="Assignment flow navigation"
        className="assignment-flow-navigation"
      >
        {this.renderLeavingModal()}
        <div className="assignment-flow-navigation-container">
          <div className="left">
            <button
              aria-label="Leave assignment flow"
              className="interactive-wrapper focus-on-light"
              onClick={this.toggleLeavingModal}
              type="button"
            >
              <img
                alt="green Quill logo"
                src={quillLogoGreenSrc}
              />
            </button>
            {this.renderLinks()}
          </div>
          <div className="right">
            {this.renderButton()}
          </div>
        </div>
        {this.renderProgressBar()}
      </nav>
    )
  }
}
